import { HttpClient } from '@angular/common/http';
import { InjectionToken } from '@angular/core';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { first, map, publishReplay, refCount, switchMap, withLatestFrom } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { Settings } from '../core';
import { UserStorage } from '../user/user-storage';
import { CategoriesStorage } from './categories-storage';
import { Acknowledgement } from '../view-history';
import { DocumentsCounterBase, DocumentsCounterReadModel } from './models/documents-counter';
import { Report } from './models/report';
import { ReportsPackage } from './models/reports-package';

export const UNREAD_REPORTS_STORAGE = new InjectionToken<UnreadReportsStorage<DocumentsCounterBase>>('DocumentsUnreadReportStorageToken');

export interface ReadDocumentMarker {
    markAsRead(reports: Report[]): Promise<void>;
}

export abstract class UnreadReportsStorage<T extends DocumentsCounterBase> implements ReadDocumentMarker {

    public get allUnread(): Observable<T[]> {
        return this.allUnreadStream;
    }

    public get docsHaveBeenRead(): Observable<ReportsPackage> {
        return this.markedAsRead$.asObservable();
    }

    private readonly allUnreadStream: Observable<T[]>;
    private readonly markedAsRead$ = new Subject<ReportsPackage>();
    private readonly updatedUnreadCounters$ = new Subject<ReportsPackage>();
    private readonly refresher = new BehaviorSubject<Observable<T[]>>(this.getUnreadCounters());

    constructor(
        private readonly settings: Settings,
        private readonly http: HttpClient,
        protected readonly categoriesStorage: CategoriesStorage,
        private readonly userStorage: UserStorage
    ) {
        const loadedUnreadCounters$ = this.refresher
            .pipe(
                switchMap(x => x),
                publishReplay(1),
                refCount()
            );
        // TODO: Bad. Mutations in the transformations
        const recalculatedOnDocumentsReading$ = this.markedAsRead$
            .pipe(
                withLatestFrom(loadedUnreadCounters$),
                map(([updatedReports, unreadCounters]: [ReportsPackage, T[]]) => {
                    return this.recountOnDocumentsReading(updatedReports, unreadCounters);
                })
            );

        const recalculatedOnCountersUpdating$ = this.updatedUnreadCounters$
            .pipe(
                withLatestFrom(loadedUnreadCounters$),
                map(([updatedReports, unreadCounters]: [ReportsPackage, T[]]) => {
                    return this.recountOnCountersUpdating(updatedReports, unreadCounters);
                })
            );

        this.allUnreadStream = merge(
            loadedUnreadCounters$,
            recalculatedOnDocumentsReading$,
            recalculatedOnCountersUpdating$
        ).pipe(
            publishReplay(1),
            refCount()
        );
    }

    /**
     * @param reports Be careful, the current method works only with reports which have the same category
     */
    public async markAsRead(reports: Report[]): Promise<void> {
        const unreadBySite = reports.filter(r => !r.isRead);

        if (unreadBySite.length !== 0) {
            const unreadUpdate: ReportsPackage = { reports: unreadBySite, category: unreadBySite[0].category };
            this.markedAsRead$.next(unreadUpdate);
        }
        
        const user = await this.userStorage.currentUser.pipe(first()).toPromise();
        reports.forEach(r => {
            r.isRead = true;
            if (!r.isReadByMe) {
                r.isReadByMe = true;
                const acknowledgement = new Acknowledgement();
                acknowledgement.user = user.metadata;
                r.viewHistory.push(acknowledgement);
            }
        });
    }

    public updateUnreads(reports: ReportsPackage) {
        if (!reports) {
            return;
        }

        this.updatedUnreadCounters$.next(reports);
    }

    public getFreshUnreadsStream() {
        this.refreshUnreads();
        return this.allUnread;
    }

    public refreshUnreads() {
        this.refresher.next(this.getUnreadCounters());
    }

    protected abstract recountOnDocumentsReading(counterUpdateInfo: ReportsPackage, unreadCounters: T[]): T[];

    protected abstract recountOnCountersUpdating(counterUpdateInfo: ReportsPackage, unreadCounters: T[]): T[];

    protected abstract getUnreadCounters(): Observable<T[]>;

    protected loadUnreadCounters<TReadModelCtor extends DocumentsCounterReadModel<T>>
        (url: string, readModelCtor: new () => TReadModelCtor): Observable<T[]> {
        return this.http.get(`${this.settings.apiUrl}/${url}`).pipe(
            map((response) => deserialize(response, readModelCtor).counters),
        );
    }
}